import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { loadChats } from "../../actions/chatActions";
import { useParams } from "react-router-dom";
import CreateChatModal from "./CreateChatModal";
import ChatListItem from "./ChatListItem";
import { selectPrivatePeerMembers } from "./systemMessageText";
import { selectIdentity } from "../../selectors/userSelectors";
import usePresenceSubscription from "../../features/presence/hooks/usePresenceSubscription";
import Loader from "../Loader";

function ChatSidebar() {
  const identity = useSelector(selectIdentity);
  const myEmail = identity?.email ?? null;
  const myUserId = identity?.userId ?? null;
  const dispatch = useDispatch();
  const chats = useSelector((state) => state.chat.chats);
  const unreadByChat = useSelector((state) => state.chat.unreadByChat);
  const membersByChat = useSelector((state) => state.chat.membersByChat);
  const activeChatId = useSelector((state) => state.chat.activeChatId);
  const loadingChats = useSelector((state) => state.chat.loadingChats);
  const error = useSelector((state) => state.chat.error);
  const presenceByUserId = useSelector((state) => state.presence?.byUserId || {});
  const { id: chatId } = useParams();

  useEffect(() => {
    dispatch(loadChats());
  }, [dispatch]);

  // ONE messenger presence owner (Sidebar always mounted by ChatsLayout).
  // Priority: active-chat ids first, then sidebar private peers; hook caps max-50.
  const presenceSubscribeIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    const pushId = (raw) => {
      if (raw == null) return;
      const n = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isSafeInteger(n) || n <= 0 || seen.has(n)) return;
      seen.add(n);
      ids.push(n);
    };

    const activeId = chatId || activeChatId;
    if (activeId != null) {
      const activeChat = (chats || []).find(
        (c) => String(c?.id) === String(activeId),
      );
      const activeMembers = membersByChat?.[activeId] || [];
      if (activeChat?.is_group) {
        // Deterministic under max-50: self first (if in group), then others.
        const selfInGroup = activeMembers.some(
          (m) => m?.user?.id != null && String(m.user.id) === String(myUserId),
        );
        if (selfInGroup) {
          pushId(myUserId);
        }
        for (const member of activeMembers) {
          pushId(member?.user?.id);
        }
      } else {
        const peers = selectPrivatePeerMembers(activeMembers, myEmail, myUserId);
        for (const member of peers) {
          pushId(member?.user?.id);
        }
      }
    }

    for (const chat of chats || []) {
      if (chat?.is_group) continue;
      const members = membersByChat?.[chat.id] || [];
      const peers = selectPrivatePeerMembers(members, myEmail, myUserId);
      for (const member of peers) {
        pushId(member?.user?.id);
      }
    }
    return ids;
  }, [chats, membersByChat, myEmail, myUserId, chatId, activeChatId]);

  usePresenceSubscription(presenceSubscribeIds);

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.detail || "Не вдалося завантажити список чатів.";

  return (
    <section className={`chat-list ${chatId ? " d-none d-lg-flex" : ""}`}>
      <section className="peoples">
        {loadingChats && (
          <div className="feature-chat__state feature-chat__state--loading" role="status" aria-live="polite">
            <Loader />
            <p className="feature-chat__state-desc">Завантаження чатів…</p>
          </div>
        )}

        {!loadingChats && error && (
          <div className="feature-chat__state feature-chat__state--error" role="alert">
            <span className="feature-chat__state-icon" aria-hidden="true">
              <i className="fa-solid fa-triangle-exclamation" />
            </span>
            <p className="feature-chat__state-title">Не вдалося завантажити чати</p>
            <p className="feature-chat__state-desc">{errorMessage}</p>
            <button
              type="button"
              className="action-btn action-btn--accent"
              onClick={() => dispatch(loadChats())}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true" />
              Спробувати знову
            </button>
          </div>
        )}

        {!loadingChats && !error && chats.length === 0 && (
          <div className="feature-chat__state" role="status">
            <span className="feature-chat__state-icon" aria-hidden="true">
              <i className="fa-regular fa-comments" />
            </span>
            <p className="feature-chat__state-title">Чатів ще немає</p>
            <p className="feature-chat__state-desc">
              Створіть новий чат, щоб почати спілкування.
            </p>
          </div>
        )}

        {!loadingChats && !error && chats.length > 0 && chats.map((chat) => {
          const members = membersByChat[chat.id] || [];
          let isPeerOnline = false;
          if (!chat?.is_group) {
            const peers = selectPrivatePeerMembers(members, myEmail, myUserId);
            const peerId = peers[0]?.user?.id;
            if (peerId != null) {
              const row = presenceByUserId[String(peerId)];
              isPeerOnline = row?.online === true;
            }
          }
          return (
            <ChatListItem
              key={chat.id}
              chat={chat}
              members={members}
              unread={unreadByChat[chat.id] || 0}
              myEmail={myEmail}
              myUserId={myUserId}
              activeChatId={activeChatId}
              isPeerOnline={isPeerOnline}
            />
          );
        })}
      </section>
      <CreateChatModal />
    </section>
  );
}

export default ChatSidebar;
